<template>
  <rest-page :fetch-endpoint="fetchEndpoint">
    <div class="retention-type-tabs">
      <button
        v-for="option in typeOptions"
        :key="option.value"
        type="button"
        class="retention-type-tab"
        :class="{ active: selectedType === option.value }"
        :data-qa="`data-retention-type-tab-${option.value}`"
        @click="selectedType = option.value"
      >
        <span class="retention-type-tab-label">{{ option.label }}</span>
        <span
          class="retention-type-tab-status"
          :class="option.active ? 'active' : 'inactive'"
          :data-qa="`data-retention-type-status-${option.value}`"
        >
          {{
            option.active
              ? $t('DataRetention_Overview_Status_Active')
              : $t('DataRetention_Overview_Status_Inactive')
          }}
        </span>
      </button>
    </div>

    <transition name="retention-fade" mode="out-in">
      <div :key="selectedType" class="retention-cards">
        <retention-execution-card
          card-kind="scheduled"
          :title="$t('DataRetention_Overview_UpcomingDeletions')"
          :retention-type="selectedType"
          :categories="activeCategoriesForSelectedType"
          :record="previewRecord"
          :policy="policy"
          :loading="previewLoading"
          :policy-active="isSelectedTypeActive"
        />

        <retention-execution-card
          card-kind="live"
          :title="$t('DataRetention_Overview_LastExecution')"
          :retention-type="selectedType"
          :categories="allCategoriesForSelectedType"
          :record="liveRecord"
          :policy="policy"
          :loading="liveLoading"
          :policy-active="isSelectedTypeActive"
        />
      </div>
    </transition>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint, projectUrl } from '@/core/helpers'
import RetentionExecutionCard from '@/modules/settings/components/RetentionExecutionCard'
import {
  RETENTION_TYPES,
  CATEGORIES_BY_TYPE
} from '@/modules/settings/enum/DataRetentionEnums'

export default {
  name: 'DataRetentionOverviewTab',

  components: {
    RestPage,
    RetentionExecutionCard
  },

  data() {
    return {
      selectedType: RETENTION_TYPES.USER_PROFILES,
      policy: null,
      previewRecord: null,
      liveRecord: null,
      previewLoading: false,
      liveLoading: false
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'retention-policy',
        params: {
          expand:
            'users_cleanup_next_run,audit_log_cleanup_next_run,campaign_audit_log_cleanup_next_run'
        },
        handler: data => {
          this.policy = data
          this.fetchCards()
        }
      })
    },

    typeOptions() {
      return [
        {
          value: RETENTION_TYPES.USER_PROFILES,
          label: this.$t('DataRetention_Overview_Type_UserProfiles'),
          active: this.isTypeActive(RETENTION_TYPES.USER_PROFILES)
        },
        {
          value: RETENTION_TYPES.PROFILE_AUDIT_LOGS,
          label: this.$t('DataRetention_Overview_Type_ProfileAuditLogs'),
          active: this.isTypeActive(RETENTION_TYPES.PROFILE_AUDIT_LOGS)
        },
        {
          value: RETENTION_TYPES.CAMPAIGN_AUDIT_LOGS,
          label: this.$t('DataRetention_Overview_Type_CampaignAuditLogs'),
          active: this.isTypeActive(RETENTION_TYPES.CAMPAIGN_AUDIT_LOGS)
        }
      ]
    },

    allCategoriesForSelectedType() {
      return CATEGORIES_BY_TYPE[this.selectedType].map(c => ({
        key: c.key,
        label: this.$t(c.i18n)
      }))
    },

    activeCategoriesForSelectedType() {
      const policy = this.policy || {}
      return CATEGORIES_BY_TYPE[this.selectedType]
        .filter(c => !c.policyField || Number(policy[c.policyField]) > 0)
        .map(c => ({
          key: c.key,
          label: this.$t(c.i18n)
        }))
    },

    isSelectedTypeActive() {
      return this.isTypeActive(this.selectedType)
    },

    canViewAuditData() {
      return this.$can('DataRetention:AuditAccess')
    }
  },

  watch: {
    selectedType() {
      this.fetchCards()
    }
  },

  methods: {
    isTypeActive(type) {
      if (!this.policy) {
        return false
      }
      if (type === RETENTION_TYPES.USER_PROFILES) {
        return Boolean(this.policy.profile_retention)
      }
      if (!this.policy.audit_log_retention) {
        return false
      }
      if (type === RETENTION_TYPES.PROFILE_AUDIT_LOGS) {
        return Number(this.policy.profile_auditlog_retention_period_months) > 0
      }
      if (type === RETENTION_TYPES.CAMPAIGN_AUDIT_LOGS) {
        return Number(this.policy.campaign_auditlog_retention_period_months) > 0
      }
      return false
    },

    fetchCards() {
      if (!this.canViewAuditData) {
        this.previewRecord = null
        this.liveRecord = null
        this.previewLoading = false
        this.liveLoading = false
        return
      }
      this.fetchPreview()
      this.fetchLive()
    },

    fetchPreview() {
      this.previewLoading = true
      this.previewRecord = null
      const type = this.selectedType

      RestApi.get(projectUrl('retention-policy/preview'), {
        params: { type, expand: 'result' }
      })
        .then(({ data }) => {
          if (type !== this.selectedType) {
            return
          }
          this.previewRecord =
            data && typeof data === 'object' && Object.keys(data).length > 0
              ? data
              : null
        })
        .catch(() => {
          if (type === this.selectedType) {
            this.previewRecord = null
          }
        })
        .finally(() => {
          if (type === this.selectedType) {
            this.previewLoading = false
          }
        })
    },

    fetchLive() {
      this.liveLoading = true
      this.liveRecord = null
      const type = this.selectedType

      RestApi.get(projectUrl('retention-policy/history'), {
        params: {
          type,
          run_type: 'live',
          'per-page': 1,
          expand: 'result'
        }
      })
        .then(({ data }) => {
          if (type !== this.selectedType) {
            return
          }
          const items = Array.isArray(data)
            ? data
            : Array.isArray(data && data.items)
            ? data.items
            : []
          this.liveRecord = items.length > 0 ? items[0] : null
        })
        .catch(() => {
          if (type === this.selectedType) {
            this.liveRecord = null
          }
        })
        .finally(() => {
          if (type === this.selectedType) {
            this.liveLoading = false
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.retention-type-tabs {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding: 4px;
  background: #f2f5f9;
  border-radius: 6px;
}

.retention-type-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  color: #5c6374;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  &.active {
    background: #ffffff;
    color: #1d87c8;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
  }
}

.retention-type-tab-status {
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  &.active {
    background: #c9efd2;
    color: #10832a;
  }

  &.inactive {
    background: #dfe3ea;
    color: #5c6374;
  }
}

.retention-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

@media (max-width: 1100px) {
  .retention-cards {
    grid-template-columns: 1fr;
  }
}

.retention-fade-enter-active,
.retention-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.retention-fade-enter,
.retention-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/components/DataRetentionOverviewTab.vue